import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { handleUnauthorizedAccess } from "@/lib/access-control";

/**
 * Reusable Pagination Component
 * Shows page navigation with previous/next buttons and page numbers
 */
export function Pagination({ 
  currentPage, 
  totalPages, 
  onPageChange,
  className = "",
  isAuthenticated = true,
  scrollToId = null  // ID of element to scroll to after page change
}) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  
  if (totalPages <= 1) return null;

  const handlePageChange = (page) => {
    // If visitor (not authenticated), redirect to registration page
    if (!isAuthenticated) {
      handleUnauthorizedAccess(navigate, { 
        message: t("pagination.register_to_see_more")
      });
      window.scrollTo(0, 0);
      return;
    }
    onPageChange(page);
    
    // Scroll to target element if specified
    if (scrollToId) {
      setTimeout(() => {
        const element = document.getElementById(scrollToId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show limited pages with ellipsis
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-9 w-9 p-0 bg-card/95 backdrop-blur-sm border-border hover:bg-primary hover:text-primary-foreground hover:border-primary disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("pagination.previous_page")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {getPageNumbers().map((page, idx) => (
        page === '...' ? (
          <span key={`ellipsis-${idx}`} className="px-2 text-foreground font-medium">...</span>
        ) : (
          <TooltipProvider key={page}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className={`h-9 w-9 p-0 text-sm font-bold ${
                    currentPage === page 
                      ? "bg-primary text-primary-foreground border-primary" 
                      : "bg-card/95 backdrop-blur-sm border-border hover:bg-primary hover:text-primary-foreground hover:border-primary"
                  }`}
                >
                  {page}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t("pagination.go_to_page", { page })}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      ))}
      
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="h-9 w-9 p-0 bg-card/95 backdrop-blur-sm border-border hover:bg-primary hover:text-primary-foreground hover:border-primary disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("pagination.next_page")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export default Pagination;
